/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vu Dinh Luan (vdluan@rsupport.com)
 *
 * @description
 *
 * @created at Mon Dec 23 2024
 **/

import { useCallback } from "react";
import styled from "@emotion/styled";

import { EventHandler } from "@rfice/eventhandler";
import { _T } from "@rfice/language";
import { Button, Column, Divider, Row, Typography, TypographyCSS, Avatar } from "@/frameworks/designStuff/components";
import { EventID, POPOVER_ID, SidebarID } from "@/frameworks/core/commons";
import { useGNBNotificationActions, usePopover, useSidebarActions } from "@/frameworks/core/hooks";
import * as S from "../private/emotion";

export default function GuestBookMyRoom({
	userInfo,
	spaceInfo,
	readKey,
	onClose
}: {
	readKey: PushServices.Space.GuestBookPosted.Data["readKey"];
	userInfo: PushServices.Space.GuestBookPosted.Data["userInfo"];
	spaceInfo: PushServices.Space.GuestBookPosted.Data["spaceInfo"];
	onClose: () => void;
}) {
	const popover = usePopover();
	const sidebarActions = useSidebarActions();
	const { handleReadNormal } = useGNBNotificationActions();

	const handleRead = useCallback(() => {
		handleReadNormal(readKey);
	}, []);

	const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		popover.hide(POPOVER_ID.BACKGROUND_UI_POPUP);
		sidebarActions.hideAll("right");
		sidebarActions.show(SidebarID.MyRoomPanel);
		EventHandler.emit(EventID.OPEN_MY_ROOM_PANEL, {
			spaceID: spaceInfo.id
		});
		handleRead();
	};

	return (
		<S.NoticeWrapperGroupJoin gap={12}>
			<Row css={{ position: "absolute", right: "20px", top: "8px" }}>
				<Button.Icon name="Close24" filled="transparent" variant="contained" size="medium" onClick={onClose} />
			</Row>
			<Row gap={16}>
				<Avatar
					kind="space_80"
					type="image"
					text={spaceInfo.name ?? ""}
					textColor="deco1_50_100"
					src={spaceInfo.thumbnailPath}
					css={{
						flex: "none"
					}}
				/>
				<Column gap={8}>
					<Typography typoSize="Head6">{_T("방명록 새 글")}</Typography>
					<Column>
						<UserName typoSize="Body3_MD">
							{_T("{{name}} 님이 방명록을 등록하였습니다.", { name: userInfo.name })}
						</UserName>
					</Column>
				</Column>
			</Row>
			<Divider type="line" />
			<Row style={{ width: "100%" }} horizonAlign="right" gap={8}>
				<Button filled="primary" onClick={handleClick}>
					{_T("방명록 확인")}
				</Button>
			</Row>
		</S.NoticeWrapperGroupJoin>
	);
}

const UserName = styled(Typography)`
	#target {
		${TypographyCSS("Title3")}
	}
	white-space: pre-wrap;
`;
